import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ReactNode, Ref } from "react";
import { useId } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";

export const inputVariants = cva(
  cn(
    "w-full rounded-sm border bg-surface px-3 text-ink",
    // 16px, always. Anything smaller makes iOS Safari zoom the page on focus,
    // which strands a fan mid-form with a viewport they have to pinch back.
    "text-input placeholder:text-ink-tertiary",
    "transition-colors duration-100 ease-standard",
    REDUCED_MOTION_SAFE,
    FOCUS_RING,
    "disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled-bg disabled:text-disabled-text",
  ),
  {
    variants: {
      invalid: {
        // Color is never the only signal — the message below the field carries
        // the meaning, and `aria-invalid` carries it to a screen reader.
        true: "border-error focus-visible:outline-error",
        false: "border-line hover:border-line-strong",
      },
      // `sm` is the 44px floor itself, not the 36px compact control height a
      // compact Button uses. A bare <input> is a replaced element and renders
      // no ::after, so the hit-slop overlay recipe in ../lib/focus.ts cannot
      // pad it out — it has to be big enough on its own, reached the same way
      // Checkbox, Radio, Select, and Tabs reach it.
      inputSize: {
        /** The platform default. 48px tall. */
        md: "h-control-height",
        /** Compact forms. 44px — the floor exactly, the smallest legal field. */
        sm: "min-h-touch-target",
      },
    },
    defaultVariants: { invalid: false, inputSize: "md" },
  },
);

export type typeInputProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "size" | "id" | "children"
> &
  Omit<VariantProps<typeof inputVariants>, "invalid"> & {
    /**
     * Required. `ui-patterns.md`: a label sits above the input, always — a
     * placeholder standing in for a label disappears the moment a fan starts
     * typing, which is exactly when they need it.
     */
    label: string;
    /** Steady helper text. Replaced by `error` when there is one. */
    hint?: string | undefined;
    /**
     * Inline validation, shown under the field. A required field left empty is
     * NOT an API failure and never goes to the error modal — it stays here.
     */
    error?: string | undefined;
    /** Marks the control required for both sighted and screen-reader users. */
    required?: boolean | undefined;
    /** Visually hides the label. It stays in the accessibility tree. */
    labelHidden?: boolean | undefined;
    /** A unit, a currency symbol, or an icon rendered inside the field. */
    prefix?: ReactNode | undefined;
    suffix?: ReactNode | undefined;
    ref?: Ref<HTMLInputElement> | undefined;
  };

/**
 * A labeled text input.
 *
 * Pick the right `type`/`inputMode` per field — a numeric keypad for a join
 * code, an email keyboard for an email — because a fan filling this in is on a
 * phone with one thumb.
 */
export const Input = ({
  label,
  hint,
  error,
  required = false,
  labelHidden = false,
  prefix,
  suffix,
  inputSize,
  className,
  ref,
  ...rest
}: typeInputProps) => {
  const generatedId = useId();
  const inputId = `${generatedId}-input`;
  const hintId = `${generatedId}-hint`;
  const errorId = `${generatedId}-error`;
  const invalid = typeof error === "string" && error.length > 0;

  return (
    <div className="flex w-full flex-col gap-1">
      <label
        htmlFor={inputId}
        className={cn("text-caption text-ink", labelHidden && "sr-only")}
      >
        {label}
        {required ? (
          <span className="text-error" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </label>

      <div className="relative flex items-center">
        {prefix ? (
          <span
            className="pointer-events-none absolute left-3 text-ink-secondary"
            aria-hidden="true"
          >
            {prefix}
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorId : hint ? hintId : undefined}
          className={cn(
            inputVariants({ invalid, inputSize }),
            prefix ? "pl-8" : undefined,
            suffix ? "pr-8" : undefined,
            className,
          )}
          {...rest}
        />
        {suffix ? (
          <span
            className="pointer-events-none absolute right-3 text-ink-secondary"
            aria-hidden="true"
          >
            {suffix}
          </span>
        ) : null}
      </div>

      {invalid ? (
        // `role="alert"` so a screen reader hears the correction without
        // having to go looking for it.
        <p id={errorId} role="alert" className="text-caption text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-caption text-ink-secondary">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
